import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Twitter, Mail, Phone, MapPin, Send } from "lucide-react";
import { BRAND } from "../mock/mock";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";

const Footer = () => {
  const { toast } = useToast();
  const [email, setEmail] = React.useState("");

  const subscribe = (e) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    toast({ title: "You're on the list!", description: "Thanks for subscribing to Agrawal Rich Dry Fruits." });
    setEmail("");
  };

  return (
    <footer className="mt-16 bg-[#2A1A15] text-[#f2e7d1]">
      {/* Newsletter */}
      <div className="bg-[#3a231d]">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-serif-brand text-2xl md:text-3xl text-white">Join the Family</h3>
            <p className="text-[#e5d3ac] mt-1 text-sm">Get 10% off your first order & festive updates — straight to your inbox.</p>
          </div>
          <form onSubmit={subscribe} className="flex w-full md:w-auto items-center gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Your email address" className="h-11 w-full md:w-80 bg-white text-[#2a1a15] border-0" />
            <Button type="submit" className="h-11 bg-gold hover:bg-[#b6944f] text-[#2a1a15] font-semibold"><Send className="h-4 w-4 mr-1.5" /> Subscribe</Button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="Agrawal" className="h-14 w-14 object-contain bg-white/95 rounded-full p-1" />
            <div>
              <div className="font-serif-brand text-white text-xl leading-tight">Agrawal Rich</div>
              <div className="text-[10px] tracking-[0.25em] text-gold">DRY FRUITS · SINCE 1973</div>
            </div>
          </div>
          <p className="mt-4 text-sm text-[#d9c9a4] leading-relaxed">Bringing you the finest selection of dry fruits, nuts & seeds for a healthier life. From our family to yours.</p>
          <div className="flex items-center gap-3 mt-5">
            {[
              { I: Instagram, href: BRAND.instagramUrl },
              { I: Facebook, href: "#" },
              { I: Youtube, href: "#" },
              { I: Twitter, href: "#" },
            ].map(({ I, href }, i) => (
              <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="h-9 w-9 rounded-full border border-[#5c3e33] flex items-center justify-center text-[#f2e7d1] hover:bg-gold hover:text-[#2a1a15] hover:border-gold transition-colors">
                <I className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-white font-serif-brand text-lg mb-4">Shop</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/shop?cat=almonds" className="hover:text-gold transition-colors">Almonds</Link></li>
            <li><Link to="/shop?cat=cashews" className="hover:text-gold transition-colors">Cashews</Link></li>
            <li><Link to="/shop?cat=pistachios" className="hover:text-gold transition-colors">Pistachios</Link></li>
            <li><Link to="/shop?cat=walnuts" className="hover:text-gold transition-colors">Walnuts</Link></li>
            <li><Link to="/shop?cat=dates" className="hover:text-gold transition-colors">Dates</Link></li>
            <li><Link to="/shop?cat=combos" className="hover:text-gold transition-colors">Gift Hampers</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-serif-brand text-lg mb-4">Company</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/about" className="hover:text-gold transition-colors">Our Story</Link></li>
            <li><Link to="/wholesale" className="hover:text-gold transition-colors">Bulk & Wholesale</Link></li>
            <li><Link to="/contact" className="hover:text-gold transition-colors">Corporate Gifting</Link></li>
            <li><Link to="/contact" className="hover:text-gold transition-colors">Contact Us</Link></li>
            <li><Link to="/about" className="hover:text-gold transition-colors">Quality Promise</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-serif-brand text-lg mb-4">Get in Touch</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-gold" /> {BRAND.address}</li>
            <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-gold" /> <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="hover:text-gold">{BRAND.phone}</a></li>
            <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-gold" /> <a href={`mailto:${BRAND.email}`} className="hover:text-gold">{BRAND.email}</a></li>
            <li className="flex items-center gap-2"><Instagram className="h-4 w-4 text-gold" /> <a href={BRAND.instagramUrl} target="_blank" rel="noopener noreferrer" className="hover:text-gold">{BRAND.instagram}</a></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[#4a2d24]">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-[12.5px] text-[#c9b590]">
          <div>© {new Date().getFullYear()} Agrawal Rich Dry Fruits. All rights reserved.</div>
          <div className="flex items-center gap-5">
            <a href="#" className="hover:text-gold">Privacy Policy</a>
            <a href="#" className="hover:text-gold">Terms of Service</a>
            <a href="#" className="hover:text-gold">Shipping & Returns</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
